import { apiOAuth, ApiOAuthAuthorizeParams } from "@/api/oauth/oauth.api.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { FullScreenCard } from "@/pages/security/components/FullScreenCard.tsx";
import { Box, Button, Checkbox, Stack, Text, Title } from "@skailer-com/uikit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

type Screen = "loading" | "consent" | "error";

const OAUTH_ERROR_KEYS: Record<string, string> = {
  invalid_scope: "oauth:error_invalid_scope",
  invalid_client: "oauth:error_invalid_client",
  unauthorized_client: "oauth:error_invalid_client",
  invalid_request: "oauth:error_invalid_request",
  unsupported_response_type: "oauth:error_invalid_request",
};

const REQUIRED_SCOPES = ["mcp:read"];

function oauthErrorKey(error: unknown): string {
  const code = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof code === "string" && OAUTH_ERROR_KEYS[code]
    ? OAUTH_ERROR_KEYS[code]
    : "oauth:generic_error";
}

export function OAuthAuthorize() {
  const { t } = useTranslation();
  useTitle(t("oauth:page_title"));

  const [searchParams] = useSearchParams();
  const [screen, setScreen] = useState<Screen>("loading");
  const [clientName, setClientName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [isSubmitting, setSubmitting] = useState(false);

  const params: ApiOAuthAuthorizeParams = useMemo(
    () => ({
      client_id: searchParams.get("client_id") ?? "",
      redirect_uri: searchParams.get("redirect_uri") ?? "",
      code_challenge: searchParams.get("code_challenge") ?? "",
      code_challenge_method: searchParams.get("code_challenge_method") ?? "S256",
      response_type: searchParams.get("response_type") ?? "code",
      scope: searchParams.get("scope") ?? "",
      state: searchParams.get("state") ?? "",
    }),
    [searchParams]
  );

  useEffect(() => {
    (async () => {
      const res = await apiOAuth.getAuthorize(params);
      if (!res.success || !res.data) {
        setErrorMsg(t(oauthErrorKey(res.error)));
        setScreen("error");
        return;
      }
      if ("redirect_to" in res.data) {
        window.location.href = res.data.redirect_to;
        return;
      }
      setClientName(res.data.client_name);
      setScopes(res.data.scopes);
      setSelectedScopes(res.data.scopes);
      setScreen("consent");
    })();
  }, []);

  const scopeTitle = (scope: string) =>
    scope === "mcp:read"
      ? t("oauth:scope_mcp_read")
      : scope === "mcp:write"
        ? t("oauth:scope_mcp_write")
        : t("oauth:scope_unknown", { scope });

  const toggleScope = (scope: string) =>
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((x) => x !== scope) : [...prev, scope]
    );

  const approve = async () => {
    setSubmitting(true);
    const res = await apiOAuth.postAuthorize({ ...params, scope: selectedScopes.join(" ") });
    if (res.success && res.data?.redirect_to) {
      window.location.href = res.data.redirect_to;
      return;
    }
    setErrorMsg(t(oauthErrorKey(res.error)));
    setScreen("error");
    setSubmitting(false);
  };

  const deny = () => {
    let url: URL;
    try {
      url = new URL(params.redirect_uri);
    } catch {
      setErrorMsg(t("oauth:error_invalid_request"));
      setScreen("error");
      return;
    }
    url.searchParams.set("error", "access_denied");
    if (params.state) url.searchParams.set("state", params.state);
    window.location.href = url.toString();
  };

  return (
    <FullScreenCard>
      <Stack gap={"sm"} className={"w-full sm:max-w-md"}>
        <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
          <Stack gap={"2xl"}>
            <div className="flex items-center text-2xl font-semibold gap-x-[6px]">
              <img src="/logo.png" alt="Skailer" className="h-[28px] w-auto" />
              <Text className={"text-[22px] leading-[24px]"}>{t("core:app_name")}</Text>
            </div>

            {screen === "loading" && <Text>{t("oauth:loading")}</Text>}

            {screen === "error" && (
              <Stack gap={"lg"}>
                <Title header={t("oauth:error_title")} size={"lg"} />
                <Text className={"text-danger"}>{errorMsg}</Text>
              </Stack>
            )}

            {screen === "consent" && (
              <Stack gap={"lg"}>
                <Title header={t("oauth:requests_access", { clientName })} size={"lg"} />
                <Text className={"text-secondary"}>{t("oauth:requests_access_description")}</Text>
                <Stack gap={"sm"}>
                  {scopes.map((scope) => {
                    const isRequired = REQUIRED_SCOPES.includes(scope);
                    return (
                      <Checkbox
                        key={scope}
                        title={scopeTitle(scope)}
                        variant={"card"}
                        checked={isRequired || selectedScopes.includes(scope)}
                        disabled={isRequired || isSubmitting}
                        onChange={() => toggleScope(scope)}
                      />
                    );
                  })}
                  <Text className={"text-secondary text-sm"}>{t("oauth:scopes_hint")}</Text>
                </Stack>
                <Stack gap={"sm"}>
                  <Button
                    type={"button"}
                    size="lg"
                    onClick={approve}
                    className="w-full"
                    isLoading={isSubmitting}
                    disabled={isSubmitting || selectedScopes.length === 0}
                  >
                    {t("oauth:allow")}
                  </Button>
                  <Button
                    type={"button"}
                    size="lg"
                    variant="secondary"
                    onClick={deny}
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {t("oauth:deny")}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>
    </FullScreenCard>
  );
}
